import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config/api';
import StashitLogo from '../components/Logo/StashitLogo';

interface SupportTicket {
  id: string;
  ticket_number: string;
  title: string;
  description: string;
  priority: string;
  status: string;
  created_at: string;
  resolved_at?: string;
  support_categories: {
    name: string;
    icon: string;
    color: string;
  };
  locations?: {
    name: string;
    address: string;
  };
  lockers?: {
    locker_number: string;
  };
  smart_stall_compartments?: {
    compartment_number: string;
  };
  profiles: {
    full_name: string;
    email: string;
    phone: string;
  };
  assigned_admin?: {
    full_name: string;
    email: string;
  };
}

interface SupportMessage {
  id: string;
  message: string;
  message_type: string;
  is_internal: boolean;
  created_at: string;
  profiles: {
    full_name: string;
    role: string;
  };
}

interface SupportAnalytics {
  totalTickets: number;
  statusDistribution: any;
  priorityDistribution: any;
  categoryDistribution: any;
  slaCompliance: {
    firstResponse: { met: number; breached: number; pending: number };
    resolution: { met: number; breached: number; pending: number };
  };
  averageResolutionTime: number;
  trends: any;
}

const AdminSupportManagement: React.FC = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [analytics, setAnalytics] = useState<SupportAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  // const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('tickets');
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    category_id: '',
    assigned_to: ''
  });
  const [newMessage, setNewMessage] = useState('');
  const [isInternalMessage, setIsInternalMessage] = useState(false);

  useEffect(() => {
    fetchTickets();
    fetchAnalytics();
  }, [filters]);

  const fetchTickets = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/support/admin/tickets?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets);
      }
    } catch (err) {
      console.error('Error fetching tickets:', err);
      console.error('Failed to fetch support tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/support/admin/analytics`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data.analytics);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
    }
  };

  const fetchTicketDetails = async (ticketId: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/support/admin/tickets/${ticketId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedTicket(data.ticket);
        setMessages(data.messages);
        setActiveTab('tickets');
      }
    } catch (err) {
      console.error('Error fetching ticket details:', err);
    }
  };

  const handleUpdateTicket = async (ticketId: string, updates: any) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/support/admin/tickets/${ticketId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        alert('Ticket updated successfully!');
        fetchTickets();
        if (selectedTicket?.id === ticketId) {
          fetchTicketDetails(ticketId);
        }
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to update ticket');
      }
    } catch (err) {
      console.error('Error updating ticket:', err);
      alert('Error updating ticket');
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedTicket) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/support/admin/tickets/${selectedTicket.id}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: newMessage.trim(),
          is_internal: isInternalMessage
        })
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(prev => [...prev, data.newMessage]);
        setNewMessage('');
        setIsInternalMessage(false);
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Error sending message');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-purple-100 text-purple-800';
      case 'pending_user': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-primary">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-primary-600">Loading support management...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-primary">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <StashitLogo className="h-8 w-auto" />
              <h1 className="ml-4 text-2xl font-bold text-primary-700">Support Management</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Admin: {user?.name}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'tickets', name: 'Support Tickets', icon: '🎫' },
              { id: 'analytics', name: 'Analytics', icon: '📊' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'tickets' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Statuses</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="pending_user">Pending User</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={filters.priority}
                    onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Priorities</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={filters.category_id}
                    onChange={(e) => setFilters({ ...filters, category_id: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Categories</option>
                    {/* Add category options here */}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                  <select
                    value={filters.assigned_to}
                    onChange={(e) => setFilters({ ...filters, assigned_to: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Admins</option>
                    <option value="unassigned">Unassigned</option>
                    {/* Add admin options here */}
                  </select>
                </div>
              </div>
            </div>

            {/* Tickets List */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Support Tickets</h2>
              </div>
              <div className="p-6">
                {tickets.length > 0 ? (
                  <div className="space-y-4">
                    {tickets.map((ticket) => (
                      <div
                        key={ticket.id}
                        className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:bg-gray-50"
                        onClick={() => fetchTicketDetails(ticket.id)}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="text-lg">{ticket.support_categories.icon}</span>
                              <h3 className="font-medium text-gray-900">{ticket.title}</h3>
                              <span className="text-sm text-gray-500">#{ticket.ticket_number}</span>
                            </div>
                            <p className="text-sm text-gray-600 mb-2 line-clamp-2">{ticket.description}</p>
                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              <span>Created: {formatDate(ticket.created_at)}</span>
                              <span>By: {ticket.profiles.full_name}</span>
                              {ticket.locations && (
                                <span>📍 {ticket.locations.name}</span>
                              )}
                              {ticket.lockers && (
                                <span>🔒 Locker {ticket.lockers.locker_number}</span>
                              )}
                              {ticket.smart_stall_compartments && (
                                <span>🏪 Stall {ticket.smart_stall_compartments.compartment_number}</span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="flex space-x-2 mb-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                                {ticket.priority}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                                {ticket.status.replace('_', ' ')}
                              </span>
                            </div>
                            {ticket.assigned_admin ? (
                              <p className="text-xs text-gray-500">
                                Assigned to: {ticket.assigned_admin.full_name}
                              </p>
                            ) : (
                              <p className="text-xs text-red-500">Unassigned</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">🎫</div>
                    <h3 className="text-xl font-medium text-gray-900 mb-2">No Support Tickets</h3>
                    <p className="text-gray-600">No support tickets found matching the current filters.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Ticket Details */}
            {selectedTicket && (
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{selectedTicket.title}</h3>
                      <p className="text-sm text-gray-600">#{selectedTicket.ticket_number}</p>
                      <p className="text-sm text-gray-600">By: {selectedTicket.profiles.full_name} ({selectedTicket.profiles.email})</p>
                    </div>
                    <div className="flex space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(selectedTicket.priority)}`}>
                        {selectedTicket.priority}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedTicket.status)}`}>
                        {selectedTicket.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  {/* Quick Actions */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-3">Quick Actions</h4>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => handleUpdateTicket(selectedTicket.id, { status: 'in_progress' })}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                      >
                        Mark In Progress
                      </button>
                      <button
                        onClick={() => handleUpdateTicket(selectedTicket.id, { status: 'resolved' })}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                      >
                        Mark Resolved
                      </button>
                      <button
                        onClick={() => handleUpdateTicket(selectedTicket.id, { status: 'closed' })}
                        className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                      >
                        Close Ticket
                      </button>
                      <button
                        onClick={() => handleUpdateTicket(selectedTicket.id, { priority: 'high' })}
                        className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700"
                      >
                        Escalate Priority
                      </button>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="space-y-4 mb-6">
                    {messages.map((message) => (
                      <div key={message.id} className={`flex ${message.profiles.role === 'admin' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                          message.profiles.role === 'admin'
                            ? message.is_internal
                              ? 'bg-purple-600 text-white'
                              : 'bg-primary-600 text-white'
                            : 'bg-gray-100 text-gray-900'
                        }`}>
                          <div className="text-xs opacity-75 mb-1">
                            {message.profiles.full_name} • {formatDate(message.created_at)}
                            {message.is_internal && ' (Internal)'}
                          </div>
                          <p className="text-sm">{message.message}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Message Input */}
                  {selectedTicket.status !== 'closed' && (
                    <form onSubmit={handleSendMessage} className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="internal-message"
                          checked={isInternalMessage}
                          onChange={(e) => setIsInternalMessage(e.target.checked)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <label htmlFor="internal-message" className="text-sm text-gray-700">
                          Internal message (not visible to user)
                        </label>
                      </div>
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Type your message..."
                          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <button
                          type="submit"
                          disabled={!newMessage.trim()}
                          className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                          Send
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'analytics' && analytics && (
          <div className="space-y-6">
            {/* Overview Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <span className="text-2xl">🎫</span>
                  </div>
                  <div className="ml-4">
                    <div className="text-2xl font-bold text-gray-900">{analytics.totalTickets}</div>
                    <div className="text-sm text-gray-600">Total Tickets</div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <span className="text-2xl">✅</span>
                  </div>
                  <div className="ml-4">
                    <div className="text-2xl font-bold text-gray-900">{analytics.slaCompliance.firstResponse.met}</div>
                    <div className="text-sm text-gray-600">SLA Met</div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <span className="text-2xl">❌</span>
                  </div>
                  <div className="ml-4">
                    <div className="text-2xl font-bold text-gray-900">{analytics.slaCompliance.firstResponse.breached}</div>
                    <div className="text-sm text-gray-600">SLA Breached</div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <span className="text-2xl">⏱️</span>
                  </div>
                  <div className="ml-4">
                    <div className="text-2xl font-bold text-gray-900">{analytics.averageResolutionTime}h</div>
                    <div className="text-sm text-gray-600">Avg. Resolution</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Distribution */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Status Distribution</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {Object.entries(analytics.statusDistribution).map(([status, count]) => (
                  <div key={status} className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">{count as number}</div>
                    <div className="text-sm text-gray-600 capitalize">{status.replace('_', ' ')}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Priority Distribution */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Priority Distribution</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(analytics.priorityDistribution).map(([priority, count]) => (
                  <div key={priority} className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">{count as number}</div>
                    <div className="text-sm text-gray-600 capitalize">{priority}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* SLA Compliance */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">SLA Compliance</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">First Response</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Met:</span>
                      <span className="text-sm font-medium text-green-600">{analytics.slaCompliance.firstResponse.met}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Breached:</span>
                      <span className="text-sm font-medium text-red-600">{analytics.slaCompliance.firstResponse.breached}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Pending:</span>
                      <span className="text-sm font-medium text-yellow-600">{analytics.slaCompliance.firstResponse.pending}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Resolution</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Met:</span>
                      <span className="text-sm font-medium text-green-600">{analytics.slaCompliance.resolution.met}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Breached:</span>
                      <span className="text-sm font-medium text-red-600">{analytics.slaCompliance.resolution.breached}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Pending:</span>
                      <span className="text-sm font-medium text-yellow-600">{analytics.slaCompliance.resolution.pending}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSupportManagement;
